import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, MessageCircle, ShieldCheck } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  captureFisioCompletoContact,
  submitFisioCompletoLead,
} from "@/lib/fisiocompleto/lead.functions";
import {
  BRANCHES,
  ELEGIBILIDADE,
  getBranchKey,
  type QualifierOption,
  type QualifierQuestion,
} from "@/lib/leads/qualifier-questions";
import { pushPixelLead } from "@/lib/leads/client-tracking";
import { formatTelefoneBR } from "@/lib/leads/phone";

type FormState = { nome: string; email: string; telefone: string };
type Resposta = { pergunta: string; resposta: string; pontos: number; dealBreaker: boolean };

export function FisioCompletoQualifier({
  open,
  onOpenChange,
  whatsappUrl,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  whatsappUrl: string;
}) {
  const submit = useServerFn(submitFisioCompletoLead);
  const captureContact = useServerFn(captureFisioCompletoContact);

  const [step, setStep] = useState(0);
  const [contato, setContato] = useState<FormState>({ nome: "", email: "", telefone: "" });
  const [respostas, setRespostas] = useState<Resposta[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [capturingContact, setCapturingContact] = useState(false);
  const [partialLeadId, setPartialLeadId] = useState<string | null>(null);
  const [result, setResult] = useState<"aprovado" | "bloqueado" | null>(null);

  const situacao = respostas[0]?.resposta ?? "";
  const branchKey = situacao ? getBranchKey(situacao) : null;
  const branch = branchKey ? BRANCHES[branchKey] : null;
  const totalSteps = useMemo(() => (branch ? 2 + branch.questions.length : 2), [branch]);
  const progress = Math.round((step / Math.max(totalSteps - 1, 1)) * 100);

  function classify(arr: Resposta[], hasDealBreaker: boolean) {
    const total = arr.reduce((s, r) => s + r.pontos, 0);
    const max = arr.length * 2;
    const pct = max > 0 ? Math.round((total / max) * 100) : 0;
    let classificacao: "quente" | "morno" | "frio" = "frio";
    if (!hasDealBreaker) {
      if (pct >= 75) classificacao = "quente";
      else if (pct >= 50) classificacao = "morno";
    }
    return { total, pct, classificacao };
  }

  async function handleContatoNext(e?: React.FormEvent | React.MouseEvent) {
    e?.preventDefault?.();
    if (!contato.nome.trim() || !contato.email.trim() || !contato.telefone.trim()) {
      toast.error("Preencha nome, e-mail e WhatsApp.");
      return;
    }
    setCapturingContact(true);
    const search =
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
    try {
      const res = await captureContact({
        data: {
          page_url: typeof window !== "undefined" ? window.location.href : "",
          nome: contato.nome.trim(),
          email: contato.email.trim(),
          telefone: contato.telefone.trim(),
          utm_source: search.get("utm_source") || "",
          utm_medium: search.get("utm_medium") || "",
          utm_campaign: search.get("utm_campaign") || "",
        },
      });
      if (res.id) {
        setPartialLeadId(res.id);
        pushPixelLead({ source: "fisiocompleto", stage: "partial" });
      }
      setStep(1);
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível salvar seus dados agora. Tente novamente.");
    } finally {
      setCapturingContact(false);
    }
  }

  async function handleAnswer(question: QualifierQuestion, option: QualifierOption) {
    const nova: Resposta = {
      pergunta: question.pergunta,
      resposta: option.label,
      pontos: option.pontos,
      dealBreaker: !!option.dealBreaker,
    };
    const novas = [...respostas, nova];
    setRespostas(novas);

    if (step === 1 && option.dealBreaker && question.id === "situacao") {
      await sendLead(novas);
      return;
    }

    const nextStep = step + 1;
    const branchKeyNow = question.id === "situacao" ? getBranchKey(option.label) : branchKey;
    const branchNow = branchKeyNow ? BRANCHES[branchKeyNow] : null;
    const totalNow = branchNow ? 2 + branchNow.questions.length : 2;

    if (nextStep >= totalNow) await sendLead(novas);
    else setStep(nextStep);
  }

  async function sendLead(arr: Resposta[]) {
    setSubmitting(true);
    const hasDealBreaker = arr.some((r) => r.dealBreaker);
    const { total, pct, classificacao } = classify(arr, hasDealBreaker);
    const search =
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();

    try {
      const res = await submit({
        data: {
          partial_lead_id: partialLeadId || undefined,
          page_url: typeof window !== "undefined" ? window.location.href : "",
          nome: contato.nome.trim(),
          email: contato.email.trim(),
          telefone: contato.telefone.trim(),
          respostas: arr.map(({ pergunta, resposta, pontos }) => ({ pergunta, resposta, pontos })),
          score_total: total,
          score_pct: pct,
          classificacao,
          utm_source: search.get("utm_source") || "",
          utm_medium: search.get("utm_medium") || "",
          utm_campaign: search.get("utm_campaign") || "",
        },
      });
      pushPixelLead({
        source: "fisiocompleto",
        classificacao: res?.classificacao ?? classificacao,
        stage: "final",
      });
      if (res?.aprovado) {
        setResult("aprovado");
        setSubmitting(false);
        if (typeof window !== "undefined") window.open(whatsappUrl, "_blank", "noopener");
      } else {
        setResult("bloqueado");
        setSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível enviar agora. Tente novamente em instantes.");
      setSubmitting(false);
    }
  }

  function handleBack() {
    if (step === 0) return;
    if (step >= 2 && respostas.length > 0) setRespostas(respostas.slice(0, -1));
    setStep(Math.max(0, step - 1));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg">
            {result === "bloqueado"
              ? "Produto exclusivo para fisioterapeutas formados"
              : result === "aprovado"
                ? "Tudo certo!"
                : "Falta só mais um passo!"}
          </DialogTitle>
        </DialogHeader>

        {result === null && (
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Etapa {step + 1} de {totalSteps}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {step === 0 && (
              <div
                className="space-y-4"
                role="form"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") {
                    e.preventDefault();
                    handleContatoNext();
                  }
                }}
              >
                <p className="text-sm text-muted-foreground">
                  Preencha seus dados para falar com o nosso time:
                </p>
                <Field
                  label="Nome *"
                  autoComplete="name"
                  value={contato.nome}
                  onChange={(v) => setContato({ ...contato, nome: v })}
                />
                <Field
                  label="E-mail *"
                  type="email"
                  value={contato.email}
                  onChange={(v) => setContato({ ...contato, email: v })}
                />
                <Field
                  label="WhatsApp com DDD *"
                  type="tel"
                  value={contato.telefone}
                  onChange={(v) => setContato({ ...contato, telefone: v })}
                />
                <p className="text-xs text-muted-foreground">
                  * Exclusivo para fisioterapeutas formados
                </p>
                <button
                  type="button"
                  onClick={() => handleContatoNext()}
                  disabled={capturingContact}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-bold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
                >
                  {capturingContact ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Salvando...
                    </>
                  ) : (
                    <>
                      Continuar <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            )}

            {step === 1 && (
              <QuestionStep
                question={ELEGIBILIDADE}
                onPick={(opt) => handleAnswer(ELEGIBILIDADE, opt)}
                onBack={handleBack}
                submitting={submitting}
              />
            )}

            {step >= 2 && branch && branch.questions[step - 2] && (
              <QuestionStep
                question={branch.questions[step - 2]}
                onPick={(opt) => handleAnswer(branch.questions[step - 2], opt)}
                onBack={handleBack}
                submitting={submitting}
              />
            )}
          </div>
        )}

        {result === "aprovado" && (
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Abrimos o WhatsApp do nosso time comercial em uma nova aba. Se não abriu, use o botão
              abaixo.
            </p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-bold text-primary-foreground transition hover:opacity-90"
            >
              <MessageCircle className="h-4 w-4" /> Falar com o time agora
            </a>
          </div>
        )}

        {result === "bloqueado" && (
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-4">
              <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <p className="text-muted-foreground">
                O Projeto Fisio Completo é exclusivo para fisioterapeutas formados. Mas temos outros
                cursos e conteúdos que podem te ajudar agora.
              </p>
            </div>
            <Link
              to="/"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-bold text-primary-foreground transition hover:opacity-90"
            >
              Conheça nossos cursos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
}) {
  const isTel = type === "tel";
  const ac = autoComplete ?? (type === "email" ? "email" : isTel ? "tel-national" : undefined);
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(isTel ? formatTelefoneBR(e.target.value) : e.target.value)}
        autoComplete={ac}
        inputMode={isTel ? "tel" : type === "email" ? "email" : undefined}
        placeholder={isTel ? "(11) 99999-9999" : undefined}
        className="w-full rounded-lg border border-border bg-background px-4 py-2.5 outline-none transition focus:ring-2 focus:ring-primary"
      />
    </label>
  );
}

function QuestionStep({
  question,
  onPick,
  onBack,
  submitting,
}: {
  question: QualifierQuestion;
  onPick: (o: QualifierOption) => void;
  onBack: () => void;
  submitting: boolean;
}) {
  return (
    <div className="space-y-4">
      <h3 className="text-base font-bold leading-snug sm:text-lg">{question.pergunta}</h3>
      <div className="space-y-2">
        {question.options.map((opt) => (
          <button
            key={opt.label}
            type="button"
            disabled={submitting}
            onClick={() => onPick(opt)}
            className="group flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3 text-left transition hover:border-primary hover:bg-primary/5 disabled:opacity-50"
          >
            <span className="text-sm">{opt.label}</span>
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <ArrowRight className="h-4 w-4 text-muted-foreground transition group-hover:text-primary" />
            )}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onBack}
        disabled={submitting}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
      </button>
    </div>
  );
}
